if (!customElements.get('other-features')) {
  class OtherFeatures extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this._onItemClick = this._onItemClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._resizeTimer = null;
      this._desktopMq = window.matchMedia('(min-width: 1025px)');
    }

    connectedCallback() {
      this.track = this.querySelector('.of-selector__track');
      this.trackWrap = this.querySelector('.of-selector__track-wrap');
      this.selectorEl = this.querySelector('.of-selector');
      this.box = this.querySelector('.of-selector__box');
      this.items = [...this.querySelectorAll('.of-selector__item')];
      this.descriptions = [...this.querySelectorAll('.of-selector__desc')];
      this.featureImages = [...this.querySelectorAll('.of-media__feature')];
      this.masterImages = [...this.querySelectorAll('.of-media__master')];
      this.boxText = this.querySelector('.of-selector__box-text');
      this.copyEl = this.querySelector('.of-selector__copy');
      this.grid = this.querySelector('.of-grid');

      if (!this.items.length) return;

      for (const item of this.items) {
        item.addEventListener('click', this._onItemClick);
      }

      this.addEventListener('keydown', this._onKeyDown);
      window.addEventListener('resize', this._onResize);
      document.addEventListener('shopify:block:select', this._onBlockSelect);

      this._refreshLayout();
      this._initModal();

      /* Everything _refreshLayout probes is usually measured BEFORE the
       * heading webfont arrives — the fallback font is narrower, so the box
       * width locks in short and the (now wrappable) title breaks a line
       * early once the real font swaps in. Faces also load lazily (on first
       * use), so listen per-batch instead of the one-shot fonts.ready. */
      this._onFontsLoadingDone = () => {
        if (this.isConnected) this._refreshLayout();
      };
      document.fonts.addEventListener('loadingdone', this._onFontsLoadingDone);
    }

    /* Remeasure the world (item widths, track geometry, box width, media
     * cap) and re-apply the active state without animating. Runs whenever
     * prior measurements go stale: connect, resize settle, webfont arrival. */
    _refreshLayout() {
      this.itemWidths = this.items.map((item) => {
        const span = item.querySelector('.of-selector__item-text');
        return span ? span.getBoundingClientRect().width : item.scrollWidth;
      });
      this._recalculate(true);
      this._activate(this.activeIndex, false);

      /* Mobile: cap the media panel to the zone above the pill/connector so
       * tall portrait assets can't run past the line. The connector sits
       * above the copy and track, so its Y is static per layout — one
       * measurement per refresh holds until the next one. */
      if (!this._desktopMq.matches) {
        const media = this.querySelector('.of-media');
        const connector = this.querySelector('.of-selector__connector');
        if (media && connector) {
          const cap = connector.getBoundingClientRect().top - media.getBoundingClientRect().top;
          if (cap > 0) media.style.setProperty('--of-media-max-height', `${Math.floor(cap)}px`);
        }
      }
    }

    disconnectedCallback() {
      for (const item of this.items) {
        item.removeEventListener('click', this._onItemClick);
      }
      this.removeEventListener('keydown', this._onKeyDown);
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.fonts.removeEventListener('loadingdone', this._onFontsLoadingDone);
      if (this._resizeTimer) clearTimeout(this._resizeTimer);
      this._destroyModal();
    }

    _onItemClick(e) {
      const index = this.items.indexOf(e.currentTarget);
      if (index < 0 || index === this.activeIndex) return;
      this._activate(index, true);
    }

    _onKeyDown(e) {
      const maxIndex = this.items.length - 1;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        const next = Math.min(this.activeIndex + 1, maxIndex);
        if (next !== this.activeIndex) {
          this._activate(next, true);
          this.items[next].focus();
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const prev = Math.max(this.activeIndex - 1, 0);
        if (prev !== this.activeIndex) {
          this._activate(prev, true);
          this.items[prev].focus();
        }
      } else if (e.key === 'Enter' || e.key === ' ') {
        const item = e.target.closest('.of-selector__item');
        if (item) {
          e.preventDefault();
          item.click();
        }
      }
    }

    _onResize() {
      if (this._resizeTimer) clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._refreshLayout(), 200);
    }

    _onBlockSelect(e) {
      if (!this.contains(e.target)) return;
      const target = e.target.closest('.of-selector__item') || e.target;
      const index = this.items.indexOf(target);
      if (index >= 0) this._activate(index, true);
    }

    _recalculate(forceMeasure = false) {
      if (!this._desktopMq.matches) {
        this.track.style.top = '';
        this.selectorEl?.style.removeProperty('--of-center');

        if (forceMeasure || !this._measuredMobile) {
          const hadTransition = this.grid.style.transition;
          this.grid.style.transition = 'none';

          for (const item of this.items) {
            const wasActive = item.classList.contains('is-active');

            item.style.transition = 'none';
            item.classList.remove('is-active');
            item.style.height = 'auto';

            const h = Math.ceil(item.getBoundingClientRect().height);
            item.style.setProperty('--natural-height', `${h}px`);

            item.style.height = '';
            if (wasActive) item.classList.add('is-active');

            item.offsetHeight;
            item.style.transition = '';
          }
          this.grid.style.transition = hadTransition;
          this._measuredMobile = true;
        }

        return;
      }

      this.itemHeight = this.items[0].offsetHeight;

      if (forceMeasure || this._collapsedCenter === undefined) {
        const hadTransition = this.grid.style.transition;
        this.grid.style.transition = 'none';

        const hadSelectorTransition = this.selectorEl ? this.selectorEl.style.transition : '';
        if (this.selectorEl) this.selectorEl.style.transition = 'none';

        this.grid.offsetHeight;

        const gridRect = this.grid.getBoundingClientRect();
        const selectorRect = this.selectorEl.getBoundingClientRect();

        const gridCenterY = gridRect.height / 2;
        const selectorTopInGrid = selectorRect.top - gridRect.top;

        this._collapsedCenter = gridCenterY - selectorTopInGrid;
        this.centerOffset = this._collapsedCenter - (this.itemHeight / 2);

        this.grid.offsetHeight;
        this.grid.style.transition = hadTransition;
        if (this.selectorEl) {
          this.selectorEl.style.transition = hadSelectorTransition;
        }
      }

      this.track.style.top = `${this.centerOffset}px`;
      this.selectorEl.style.setProperty('--of-center', `${this._collapsedCenter}px`);
    }

    _updateCopyHeight(animate) {
      if (!this.copyEl) return;
      const active = this.descriptions[this.activeIndex];
      if (!active) return;

      /* The descs are absolutely stacked (crossfade), so the copy box cannot
       * auto-size — set its height from the ACTIVE desc so the plate grows
       * and shrinks with the text. A feature without copy hides the plate
       * entirely (is-empty; on desktop it is a white background card that
       * would otherwise float empty). Desktop adds the box's own padding —
       * the desc sits inset by it; mobile is padding-less. */
      const isEmpty = active.textContent.trim() === '';
      this.copyEl.classList.toggle('is-empty', isEmpty);

      let height = active.scrollHeight;
      if (this._desktopMq.matches) {
        const styles = getComputedStyle(this.copyEl);
        height += parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      }

      if (!animate) {
        this.copyEl.style.transition = 'none';
      }
      this.copyEl.style.height = `${height}px`;
      if (!animate) {
        this.copyEl.offsetHeight;
        this.copyEl.style.transition = '';
      }
    }

    _flyTextToBox(srcRect, text) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return;

      if (this._flyClone) {
        this._flyClone.remove();
        this._flyClone = null;
      }

      const styles = getComputedStyle(this.boxText);
      const destRect = this.boxText.getBoundingClientRect();
      const destPadLeft = parseFloat(styles.paddingLeft) || 0;
      const destPadRight = parseFloat(styles.paddingRight) || 0;
      const destPadTop = parseFloat(styles.paddingTop) || 0;

      /* Clone the node so its typography tracks the class instead of a
       * hand-copied property list, laid out at the pill text's FINAL content
       * width (the box text is width-pinned by _activate, so destRect is
       * already settled mid-transition) — it wraps exactly like the text it
       * reveals. cssText wipes the inherited inline styles; padding is
       * stripped because the clone flies to the text origin, not the box. */
      const clone = this.boxText.cloneNode(true);
      clone.textContent = text;
      clone.style.cssText = `
        position:fixed;left:${srcRect.left}px;top:${srcRect.top}px;
        width:${destRect.width - destPadLeft - destPadRight}px;
        padding:0;margin:0;max-width:none;opacity:1;
        z-index:100;pointer-events:none;
      `;
      document.body.appendChild(clone);
      this._flyClone = clone;

      this.boxText.style.opacity = '0';
      const dx = destRect.left + destPadLeft - srcRect.left;
      const dy = destRect.top + destPadTop - srcRect.top;

      clone.animate([
        { transform: 'translate(0, 0)' },
        { transform: `translate(${dx}px, ${dy}px)` }
      ], {
        duration: 350,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'forwards'
      }).onfinish = () => {
        clone.remove();
        this._flyClone = null;
        this.boxText.style.opacity = '';
      };
    }

    _initModal() {
      const section = this.closest('[id^="shopify-section-"]');
      this._modal = section?.querySelector('.of-modal');
      if (!this._modal) return;

      /* Hoist the dialog to <body>. On ski/boot/protection PDPs every
       * post-story .shopify-section is its own stacking context (z:3, the
       * parked-hero cover rule in section-product-about.css) — left inline,
       * the fixed overlay can never paint above the sibling sections that
       * follow, whatever its z-index. At body level its z-index:101 resolves
       * in the root context, same band as the other theme modals. The node
       * move keeps all listeners; _destroyModal takes the hoisted node along
       * on section teardown, and the sweep below catches a stray copy left
       * by a theme-editor section re-render. */
      for (const stale of document.querySelectorAll(`body > .of-modal[id="${this._modal.id}"]`)) {
        if (stale !== this._modal) stale.remove();
      }
      document.body.appendChild(this._modal);

      this._modalGrid = this._modal.querySelector('.of-modal__grid');
      this._modalItems = [...this._modal.querySelectorAll('.of-modal__feature-item')];
      this._modalPanels = [...this._modal.querySelectorAll('.of-modal__detail-panel')];
      this._modalBackBtns = [...this._modal.querySelectorAll('.of-modal__back')];
      this._onModalItemClick = this._onModalItemClick.bind(this);
      this._onModalKeyDown = this._onModalKeyDown.bind(this);
      this._onModalBackClick = this._onModalBackClick.bind(this);

      for (const item of this._modalItems) {
        item.addEventListener('click', this._onModalItemClick);
      }
      for (const btn of this._modalBackBtns) {
        btn.addEventListener('click', this._onModalBackClick);
      }
      this._modal.addEventListener('keydown', this._onModalKeyDown);

      this._modalObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.attributeName === 'open' && !this._modal.hasAttribute('open')) {
            this._modalGrid?.classList.remove('is-detail-open');
          }
        }
      });
      this._modalObserver.observe(this._modal, { attributes: true });
    }

    _destroyModal() {
      if (!this._modal) return;
      for (const item of this._modalItems) {
        item.removeEventListener('click', this._onModalItemClick);
      }
      for (const btn of this._modalBackBtns) {
        btn.removeEventListener('click', this._onModalBackClick);
      }
      this._modal.removeEventListener('keydown', this._onModalKeyDown);
      this._modalObserver?.disconnect();
      this._modal.remove(); // hoisted to <body> — must not outlive the section
    }

    _onModalItemClick(e) {
      const item = e.currentTarget;
      const index = item.dataset.modalIndex;
      this._activateModalFeature(index);

      if (!this._desktopMq.matches && this._modalGrid) {
        this._modalGrid.classList.add('is-detail-open');
      }
    }

    _onModalBackClick() {
      this._modalGrid?.classList.remove('is-detail-open');
    }

    _onModalKeyDown(e) {
      const focused = document.activeElement;
      if (!focused?.classList.contains('of-modal__feature-item')) return;

      const idx = this._modalItems.indexOf(focused);
      if (idx < 0) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        const next = Math.min(idx + 1, this._modalItems.length - 1);
        this._modalItems[next].focus();
        this._activateModalFeature(this._modalItems[next].dataset.modalIndex);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const prev = Math.max(idx - 1, 0);
        this._modalItems[prev].focus();
        this._activateModalFeature(this._modalItems[prev].dataset.modalIndex);
      }
    }

    _activateModalFeature(index) {
      for (const item of this._modalItems) {
        const isActive = item.dataset.modalIndex === index;
        item.classList.toggle('is-active', isActive);
        // aria-pressed, not aria-selected: the markup declares these as toggle
        // buttons inside a role="group", not options inside a listbox (see the
        // comment blocks in sections/other-features.liquid). aria-selected is
        // only legal on option/tab/row/gridcell, so writing it here would
        // reintroduce the aria-allowed-attr failure the markup change fixed.
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
      for (const panel of this._modalPanels) {
        panel.classList.toggle('is-active', panel.dataset.modalPanel === index);
      }
    }

    _activate(index, animate) {
      const prevIndex = this.activeIndex;
      this.activeIndex = index;

      let srcRect = null;
      if (!this._desktopMq.matches && animate && prevIndex !== index) {
        const span = this.items[index].querySelector('.of-selector__item-text');
        if (span) srcRect = span.getBoundingClientRect();
      }

      for (const [i, item] of this.items.entries()) {
        const isActive = i === index;
        item.classList.toggle('is-active', isActive);
        // aria-pressed, not aria-selected: the markup declares these as toggle
        // buttons inside a role="group", not options inside a listbox (see the
        // comment blocks in sections/other-features.liquid). aria-selected is
        // only legal on option/tab/row/gridcell, so writing it here would
        // reintroduce the aria-allowed-attr failure the markup change fixed.
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }

      if (this._desktopMq.matches && this.itemHeight) {
        this.track.style.setProperty(
          '--of-translate-y',
          `${-index * this.itemHeight}px`
        );
      }

      for (const [i, desc] of this.descriptions.entries()) {
        desc.classList.toggle('is-active', i === index);
      }

      this._updateCopyHeight(animate);

      if (this.boxText) {
        const span = this.items[index].querySelector('.of-selector__item-text');
        if (span) this.boxText.textContent = span.textContent;
      }

      if (this._desktopMq.matches) {
        const textWidth = this.itemWidths[index] || 100;
        this.box.style.setProperty('--of-box-width', `${textWidth + 40}px`);
      } else if (this.boxText) {
        const probe = this.boxText.cloneNode(true);
        /* max-width:none — the class clamps to 100% of the box, which is the
         * box's OLD width here and would cap the probe at it. */
        probe.style.cssText = 'position:absolute;visibility:hidden;width:auto;max-width:none;white-space:nowrap;';
        this.box.appendChild(probe);
        /* ceil of the fractional width — offsetWidth rounds and a probe even
         * 0.5px short makes the now-wrappable text break one line early.
         * oldBoxWidth reads in the same layout pass (the abspos probe cannot
         * affect the box width). */
        const naturalWidth = Math.ceil(probe.getBoundingClientRect().width);
        const oldBoxWidth = animate ? this.box.getBoundingClientRect().width : 0;
        probe.remove();

        /* FLIP the width change: jump to the final width unanimated, pin the
         * text to its settled layout (is-text-pinned + --of-box-text-width,
         * see the mobile CSS), then replay the border transition from the
         * old width. Pinned, the text lays out at its FINAL width from frame
         * one and never re-wraps while the box animates around it. */
        this.box.style.transition = 'none';
        this.box.classList.remove('is-text-pinned');
        this.box.style.setProperty('--of-box-width', `${naturalWidth + 2}px`);
        const finalTextWidth = this.boxText.getBoundingClientRect().width;
        this.box.style.setProperty('--of-box-text-width', `${finalTextWidth}px`);
        this.box.classList.add('is-text-pinned');
        if (animate) this.box.style.width = `${oldBoxWidth}px`;
        this.box.offsetWidth;
        this.box.style.transition = '';
        if (animate) this.box.style.width = '';
      }

      if (srcRect) {
        this._flyTextToBox(srcRect, this.items[index].querySelector('.of-selector__item-text').textContent);
      }

      const nextFeatureImg = this.querySelector(
        `.of-media__feature[data-feature="${index}"]`
      );
      const masterActive = this.masterImages.some((m) => m.classList.contains('is-active'));
      const leavingEls = [];
      const enteringEls = [];

      if (nextFeatureImg) {
        if (masterActive) {
          for (const m of this.masterImages) {
            if (m.classList.contains('is-active')) leavingEls.push(m);
          }
          enteringEls.push(nextFeatureImg);
        } else {
          for (const img of this.featureImages) {
            if (img.classList.contains('is-active') && img !== nextFeatureImg) {
              leavingEls.push(img);
            }
          }
          enteringEls.push(nextFeatureImg);
        }
      } else if (!masterActive) {
        for (const img of this.featureImages) {
          if (img.classList.contains('is-active')) leavingEls.push(img);
        }
        enteringEls.push(...this.masterImages);
      }

      /* Both sides show the SAME image (features sharing one asset, or a
       * feature matching the master shot): the clip-path wipe would replay
       * over identical pixels — swap the active layer instantly instead,
       * transitions suspended for the swap only. */
      const layerSrc = (el) => {
        const img = el?.querySelector('.of-media__img');
        return img ? img.currentSrc || img.src : null;
      };
      if (
        leavingEls.length > 0 &&
        enteringEls.length > 0 &&
        layerSrc(leavingEls[0]) !== null &&
        layerSrc(leavingEls[0]) === layerSrc(enteringEls[0])
      ) {
        const swapEls = [...leavingEls, ...enteringEls];
        for (const el of swapEls) el.style.transition = 'none';
        for (const el of leavingEls) el.classList.remove('is-active', 'is-leaving');
        for (const el of enteringEls) {
          el.classList.remove('is-leaving');
          el.classList.add('is-active');
        }
        enteringEls[0].offsetHeight; // commit the un-transitioned state
        for (const el of swapEls) el.style.transition = '';
        return;
      }

      const startSwap = () => {
        if (leavingEls.length > 0) {
          for (const el of leavingEls) {
            el.classList.remove('is-active');
            el.classList.add('is-leaving');
          }

          if (enteringEls.length > 0) {
            leavingEls[0].addEventListener('transitionend', () => {
              for (const el of leavingEls) el.classList.remove('is-leaving');
              for (const el of enteringEls) {
                el.classList.remove('is-leaving');
                el.classList.add('is-active');
              }
            }, { once: true });
          } else {
            leavingEls[0].addEventListener('transitionend', () => {
              for (const el of leavingEls) el.classList.remove('is-leaving');
            }, { once: true });
          }
        } else if (enteringEls.length > 0) {
          for (const el of enteringEls) {
            el.classList.remove('is-leaving');
            el.classList.add('is-active');
          }
        }
      };

      /* Never run the wipe over a not-yet-loaded image — the reveal would
       * play on an empty layer and the photo would pop in afterwards. Wait
       * for the entering images to decode (decode() also force-starts lazy
       * loads), capped at 4s so a stalled network can't wedge feature
       * switching; the current layer simply stays put in the meantime. Every
       * _activate bumps the token, so a newer switch supersedes a pending
       * one and the stale swap never fires. */
      const swapToken = (this._imgSwapToken = (this._imgSwapToken || 0) + 1);
      const pendingImgs = enteringEls
        .flatMap((el) => [...el.querySelectorAll('.of-media__img')])
        .filter((img) => img.src && !(img.complete && img.naturalWidth > 0));
      if (pendingImgs.length > 0) {
        Promise.race([
          Promise.all(pendingImgs.map((img) => img.decode().catch(() => {}))),
          new Promise((resolve) => setTimeout(resolve, 4000)),
        ]).then(() => {
          if (this._imgSwapToken !== swapToken || this.activeIndex !== index) return;
          startSwap();
        });
        return;
      }

      startSwap();
    }
  }

  customElements.define('other-features', OtherFeatures);
}
